import { useState, useCallback, SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  Typography,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import Diversity1OutlinedIcon from '@mui/icons-material/Diversity1Outlined';
import SettingsIcon from '@mui/icons-material/Settings';

import { TeamsModal } from '@/components/TeamsModal/TeamsModal';
import { Autocomplete } from '@/components/shared/Autocomplete/Autocomplete';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useLoading } from '@/shared/context/UIContextProvider';

import {
  useAuth,
  ImpersonatedTeamOptionType,
  defaultImpersonatedTeamOption,
} from '@/shared/context/AuthProvider';

const MainAppUserInfo = () => {
  const navigate = useNavigate();
  const {
    userData,
    signOut,
    setSelectedOption,
    selectedOption,
    teamOptions,
    isEditMode,
    setIsEditMode,
  } = useAuth();

  interface AutherizationUrlResultType {
    data: {
      authorizationUrl: string;
    };
  }

  const { createLoading } = useLoading();

  const connectGmail = async () => {
    const functions = getFunctions();
    // call the onCall function autherizationUrl
    const getGmailAutherizationUrl = httpsCallable(
      functions,
      'getGmailAuthorizationUrl'
    );
    const result =
      (await getGmailAutherizationUrl()) as AutherizationUrlResultType;

    console.log('done with google');
    if (result.data && result.data.authorizationUrl) {
      window.location.href = result.data.authorizationUrl;
    }
  };

  const handleGmailConnect = () => {
    const loader = createLoading();

    loader.startLoading({
      title: 'Contacting Google...',
    });
    connectGmail().then(() => {
      // loader.stopLoading();
    });
  };

  const connectDocusign = async () => {
    const functions = getFunctions();
    // call the onCall function autherizationUrl
    const getDocuSignAuthURL = httpsCallable(functions, 'getDocusignOAuthURL');
    const result = (await getDocuSignAuthURL()) as AutherizationUrlResultType;

    console.log('done with docusign');
    if (result.data && result.data.authorizationUrl) {
      window.location.href = result.data.authorizationUrl;
    } else {
      console.log('Error with docusign', result);
    }
  };

  const handleDocuSignConnect = () => {
    const loader = createLoading();

    loader.startLoading({
      title: 'Contacting Docusign...',
    });
    connectDocusign().then(() => {
      loader.stopLoading();
    });
  };

  const [openTeamModal, setOpenTeamModal] = useState(false);
  const [impersonatedTeamInputFilter, setImpersonatedTeamInputFilter] =
    useState('');

  const handleChangeSelectedOption = (
    _event: SyntheticEvent<Element, Event>,
    value: ImpersonatedTeamOptionType | null
  ) => {
    if (value) {
      setSelectedOption(value);
    } else {
      setSelectedOption(defaultImpersonatedTeamOption);
    }
  };

  const handleChangeFilter = (
    _event: SyntheticEvent<Element, Event>,
    newInputValue: string
  ) => {
    setImpersonatedTeamInputFilter(newInputValue);
  };

  const handleOpenTeamModal = useCallback(() => {
    setOpenTeamModal(true);
  }, []);

  const handleCloseTeamModal = useCallback(() => {
    setOpenTeamModal(false);
  }, []);

  const buttonSx = {
    color: '#e7e5e4 !important', // stone-200
    borderColor: 'rgba(255, 255, 255, 0.1) !important',
    lineHeight: '14px',
    justifyContent: 'flex-start',
    '&:hover, &:focus': {
      color: '#3b82f6 !important',
      borderColor: '#3b82f6 !important',
      backgroundColor: 'rgba(59, 130, 246, 0.1) !important',
      outline: 'none',
    },
  };

  return (
    <>
      <Stack className="p-16 w-[300px]">
        <Stack direction="row" alignItems="center" gap="12px">
          <Avatar variant="square" className="rounded-md bg-zinc-800 text-stone-200" />

          <Stack>
            <Typography variant="caption" className="text-stone-200 font-semibold">
              {userData?.fullName}
            </Typography>
            <Typography variant="caption2" className="text-stone-400">
              {userData?.email}
            </Typography>
          </Stack>
        </Stack>

        <div className="my-16 border-t border-white/10" />

        <Stack gap="8px">
          {userData?.isDev ? (
            <FormControlLabel
              className="ml-0 mr-auto"
              value="start"
              control={
                <Switch
                  color="primary"
                  checked={isEditMode}
                  onChange={(e) => setIsEditMode(e.target.checked)}
                />
              }
              slotProps={{
                typography: {
                  className: 'text-stone-200 text-sm',
                },
              }}
              label="Edit Mode"
              labelPlacement="end"
            />
          ) : null}

          {userData?.isDev ? (
            <Button
              variant="outlined"
              onClick={handleOpenTeamModal}
              className="w-full"
              color="primary"
              sx={buttonSx}
              startIcon={<Diversity1OutlinedIcon className="text-lg" />}
            >
              Manage Teams
            </Button>
          ) : null}

          {userData?.isDev ? (
            <Autocomplete
              value={selectedOption}
              onChange={handleChangeSelectedOption}
              inputValue={impersonatedTeamInputFilter}
              onInputChange={handleChangeFilter}
              options={teamOptions}
              getOptionLabel={(option) => option.label}
              fullWidth
              className="w-full"
            />
          ) : null}

          {userData?.isDev && <div className="my-4" />}

          <Button
            variant="outlined"
            onClick={() => navigate('/settings/process')}
            className="w-full"
            color="primary"
            sx={buttonSx}
            startIcon={<SettingsIcon className="text-lg" />}
          >
            Process Settings
          </Button>

          <Button
            variant="outlined"
            onClick={handleGmailConnect}
            className="w-full"
            color="primary"
            sx={buttonSx}
          >
            Connect Gmail
          </Button>

          <Button
            variant="outlined"
            onClick={handleDocuSignConnect}
            className="w-full"
            color="primary"
            sx={buttonSx}
          >
            Connect Docusign
          </Button>

          <div className="my-8 border-t border-white/10" />

          <Button
            variant="outlined"
            onClick={signOut}
            className="w-full"
            color="primary"
            sx={buttonSx}
          >
            Log Out
          </Button>
        </Stack>
      </Stack>
      <TeamsModal open={openTeamModal} onClose={handleCloseTeamModal} />
    </>
  );
};

export default MainAppUserInfo;
